<template>
  <div class="container">
    <div class="row">
      <div class="col-12" ref="contentFrame">
        <b-tabs
          content-class="mt-3"
          active-nav-item-class="font-weight-bold"
          v-model="currentTab"
        >
          <b-tab :title="$t('scheduler.campaign')" active>
            <b-form
              class="p-2"
              @submit.prevent="onSubmit"
              :method="'POST'"
              :action="route"
            >
              <input type="hidden" name="_token" :value="csrf" />
              <input type="hidden" name="_method" :value="method" />

              <common-loading
                :message="$t('scheduler.fetching_campaign')"
                v-if="states.fetching"
              />

              <b-alert
                :show="
                  campaignId && !(permissions.indexOf('scheduler-edit') !== -1)
                "
                variant="warning"
              >
                <em class="fa fa-info-circle mr-2"></em>
                {{ $t("commons.read_only") }}
              </b-alert>

              <div class="row">
                <div class="col-lg-6 pr-3 pl-3">
                  <b-form-group
                    id="fieldset-name"
                    label-for="name"
                    label-size="lg"
                    label-class="font-weight-bold pb-0"
                    class="required mb-5 pb-0"
                  >
                    <template slot="label">
                      <span v-text="$t('scheduler.name')" />
                    </template>
                    <b-form-input
                      id="name"
                      name="name"
                      v-model.trim="name"
                      :placeholder="$t('scheduler.enter_name')"
                      :disabled="disabled || disableForm"
                    />
                    <div v-if="hasError('name')">
                      <div
                        class="alert alert-danger mt-1"
                        v-if="states.showErrors"
                        v-text="$t('commons.required_field_error')"
                      ></div>
                    </div>
                  </b-form-group>
                </div>
                <div class="col-lg-6 pr-3 pl-3">
                  <b-form-group
                    id="fieldset-description"
                    label-for="description"
                    label-size="lg"
                    label-class="font-weight-bold pb-0"
                    class="required mb-5 pb-0"
                  >
                    <template slot="label">
                      <span v-text="$t('scheduler.description')" />
                    </template>
                    <b-form-input
                      id="description"
                      name="description"
                      v-model.trim="description"
                      :placeholder="$t('scheduler.enter_description')"
                      :disabled="disabled || disableForm"
                    />
                    <div v-if="hasError('description')">
                      <div
                        class="alert alert-danger mt-1"
                        v-if="states.showErrors"
                        v-text="$t('commons.required_field_error')"
                      ></div>
                    </div>
                  </b-form-group>
                </div>
              </div>

              <div class="row justify-content-around">
                <div class="col col-md-6 d-flex mb-5" v-if="campaignId != null">
                  <b-card
                    bg-variant="light"
                    class="w-100 border border-scheduler"
                  >
                    <div
                      class="text-left h5 mb-3 font-weight-bold text-primary"
                    >
                      Audience
                    </div>
                    <div class="text-left mb-5">
                      <span
                        v-text="$t('scheduler.people_to_receive_message')"
                      ></span>
                      <b-overlay :show="fetching_messages" opacity="0.6">
                        <template #overlay>
                          <div class="d-flex align-items-center">
                            <b-spinner
                              small
                              type="grow"
                              variant="secondary"
                            ></b-spinner>
                            <b-spinner type="grow" variant="dark"></b-spinner>
                            <b-spinner
                              small
                              type="grow"
                              variant="secondary"
                            ></b-spinner>
                            <!-- We add an SR only text for screen readers -->
                            <span class="sr-only">Please wait...</span>
                          </div>
                        </template>
                        <h6 class="bold mt-5">
                          {{ $t("scheduler.people") }}:
                          <b>{{ statistics.totalPersons }}</b>
                        </h6>
                        <h6 class="bold">
                          {{ $t("scheduler.opted_out") }}:
                          <b>{{ statistics.optedOutPersons }}</b>
                        </h6>
                      </b-overlay>
                    </div>
                    <div class="container">
                      <div class="row justify-content-between">
                        <div class="col text-left p-0">
                          <b-button
                            variant="light"
                            class="mt-2 text-nowrap"
                            @click="setTab(2)"
                            :disabled="
                              disabled ||
                              disableForm ||
                              !(
                                permissions.indexOf('scheduler-edit') !== -1 &&
                                permissions.indexOf('scheduler-read') !== -1
                              )
                            "
                          >
                            <em class="fa fa-edit"></em>
                            <span
                              v-text="$t('scheduler.edit_smart_list')"
                            ></span>
                          </b-button>
                        </div>
                      </div>
                    </div>
                  </b-card>
                </div>
                <div class="col col-md-6 d-flex mb-5">
                  <b-card
                    bg-variant="light"
                    class="w-100 text-center border border-scheduler"
                  >
                    <div
                      class="h5 mb-3 font-weight-bold text-left text-primary"
                    >
                      Message Template
                    </div>
                    <div class="text-left mb-5">
                      <div class="mb-2">
                        <span v-text="$t('scheduler.choose_mt')"></span>
                      </div>
                      <b-overlay :show="fetching_messages" opacity="0.6">
                        <template #overlay>
                          <div class="d-flex align-items-center">
                            <b-spinner
                              small
                              type="grow"
                              variant="secondary"
                            ></b-spinner>
                            <b-spinner type="grow" variant="dark"></b-spinner>
                            <b-spinner
                              small
                              type="grow"
                              variant="secondary"
                            ></b-spinner>
                            <!-- We add an SR only text for screen readers -->
                            <span class="sr-only">Please wait...</span>
                          </div>
                        </template>
                        <b-input-group
                          :aria-hidden="fetching_messages ? 'true' : null"
                        >
                          <vue-multiselect
                            v-model="template"
                            :options="outbound_messages"
                            :placeholder="$t('scheduler.select_message')"
                            track-by="id"
                            value="id"
                            label="name"
                            :allow-empty="false"
                            :selectLabel="''"
                            :deselectLabel="''"
                            :selectedLabel="''"
                            :disabled="disabled || disableForm"
                            @input="pagination.page = 1"
                          ></vue-multiselect>
                          <div v-if="hasError('template')">
                            <div
                              class="alert alert-danger mt-1"
                              v-if="states.showErrors"
                              v-text="$t('commons.required_field_error')"
                            ></div>
                          </div>
                        </b-input-group>
                      </b-overlay>
                    </div>
                  </b-card>
                </div>
                <div class="col col-md-6 d-flex mb-5">
                  <b-card
                    bg-variant="light"
                    class="w-100 border border-scheduler"
                  >
                    <div
                      class="text-left h5 mb-3 font-weight-bold text-primary"
                    >
                      Schedule
                    </div>
                    <div class="text-left">
                      <div class="mb-2">
                        <span v-text="$t('scheduler.select_datetime')"></span>
                      </div>
                      <div class="mb-4">
                        <b-form-group
                          id="fieldset-execute-datetime"
                          label-for="execute-datetime"
                          label-size="lg"
                          label-class="font-weight-bold pb-0"
                          class="required"
                        >
                          <b-form-input
                            id="execute-datetime-disabled"
                            name="execute-datetime-disabled"
                            v-model.trim="executeDateTime"
                            :placeholder="$t('scheduler.enter_name')"
                            disabled
                            v-if="disabled || disableForm"
                          />
                          <datetime
                            id="execute-datetime"
                            name="execute-datetime"
                            v-model="executeDateTime"
                            v-if="!disabled && !disableForm"
                          >
                            <input name="nada" value="nada" disabled="true" />
                          </datetime>
                        </b-form-group>
                      </div>
                      <div class="mb-2">
                        <span v-text="$t('scheduler.timezone')"></span>
                      </div>
                      <b-form-group
                        id="fieldset-timezone"
                        label-class="font-weight-bold pb-0"
                        class="required text-left"
                      >
                        <b-input-group
                          :aria-hidden="fetching_messages ? 'true' : null"
                        >
                          <vue-multiselect
                            v-model="timezone"
                            :options="timeZoneNamesObj"
                            :placeholder="$t('scheduler.select_timezone')"
                            class="multiselect__input"
                            track-by="id"
                            value="id"
                            label="name"
                            :allow-empty="false"
                            :selectLabel="''"
                            :deselectLabel="''"
                            :selectedLabel="''"
                            :disabled="disabled || disableForm"
                          >
                          </vue-multiselect>
                          <div v-if="hasError('timezone')">
                            <div
                              class="alert alert-danger mt-1"
                              v-if="states.showErrors"
                              v-text="$t('commons.required_field_error')"
                            ></div>
                          </div>
                        </b-input-group>
                      </b-form-group>
                    </div>
                  </b-card>
                </div>
                <div class="col col-md-6 d-flex mb-5" v-if="campaignId">
                  <b-card
                    bg-variant="light"
                    class="w-100 text-center font-weight-bold border border-scheduler"
                  >
                    <div
                      class="text-left h5 mb-3 font-weight-bold text-primary"
                    >
                      Approval
                    </div>
                    <div class="text-left mb-2 h6">
                      <strong v-text="$t('scheduler.status')"></strong>:
                      {{ status }}
                    </div>
                    <b-form-group
                      v-if="campaignId != null"
                      id="fieldset-active"
                      label-for="active"
                      label-size="lg"
                      label-class="font-weight-bold pb-0"
                      class="text-left"
                    >
                      <template slot="label">
                        <span v-text="$t('commons.active')" />
                      </template>

                      <toggle-button
                        v-model="isActive"
                        :labels="{ checked: 'On', unchecked: 'Off' }"
                        :sync="true"
                      />
                    </b-form-group>
                    <div class="text-left">
                      <b-button
                        type="button"
                        variant="primary"
                        @click="confirmUpdateCampaign"
                        :disabled="
                          status != 'DRAFT' ||
                          disabled ||
                          disableForm ||
                          !(
                            permissions.indexOf('message-edit') !== -1 &&
                            permissions.indexOf('message-read') !== -1
                          )
                        "
                      >
                        <span v-text="$t('scheduler.update')"></span>
                      </b-button>
                      <b-button
                        type="button"
                        variant="danger"
                        :disabled="cancelDisabled"
                        @click="confirmCancelCampaign"
                      >
                        <span v-text="$t('scheduler.cancel')"></span>
                      </b-button>
                      <b-button
                        type="button"
                        variant="info"
                        @click="scheduleCampaign"
                        :disabled="
                          status != 'DRAFT' ||
                          disabled ||
                          disableForm ||
                          !(
                            permissions.indexOf('message-edit') !== -1 &&
                            permissions.indexOf('message-read') !== -1
                          )
                        "
                      >
                        <span v-text="$t('scheduler.schedule')"></span>
                      </b-button>
                    </div>
                  </b-card>
                </div>
              </div>
              <b-form-group v-if="!campaignId">
                <b-button
                  type="submit"
                  variant="primary"
                  v-text="$t('buttons.save')"
                  :disabled="
                    disabled ||
                    disableForm ||
                    !(
                      permissions.indexOf('action-edit') !== -1 &&
                      permissions.indexOf('action-read') !== -1
                    )
                  "
                ></b-button>
                <b-button
                  class="btn btn-danger"
                  v-text="$t('buttons.cancel')"
                  :href="$routes('program.campaigns.index')"
                  :disabled="disabled || disableForm"
                ></b-button>
              </b-form-group>

              <common-loading
                :message="$t('scheduler.saving')"
                v-if="states.saving"
              />
              <b-alert
                variant="danger"
                :show="states.showErrors && hasErrors()"
                v-text="$t('commons.form_error')"
              />
              <b-alert
                variant="success"
                :show="states.showSuccess"
                v-text="$t('scheduler.saved_successfully')"
              />

              <b-alert variant="danger" :show="errorMessage != null">
                <em class="fa fa-times-circle mr-2"></em>
                <span v-html="errorMessage"></span>
              </b-alert>
            </b-form>
          </b-tab>
          <b-tab :title="$t('scheduler.statistics')" v-if="campaignId" lazy>
            <Statistics :statistics="statistics" :status="status"> </Statistics>
          </b-tab>
          <b-tab title="Smart List" v-if="campaignId" lazy>
            <SmartList
              :permissions="permissions"
              :campaignId="campaignId"
              :status="status"
            ></SmartList>
          </b-tab>
          <b-tab title="Persons" v-if="campaignId != null" lazy>
            <MemberList :campaignId="campaignId" :permissions="permissions">
            </MemberList>
          </b-tab>
          <b-tab title="History" v-if="campaignId" lazy>
            <b-card-text>
              <show-history
                :id="campaignId"
                :instance-id="instanceId"
                :callback="CampaignApi.find"
              ></show-history>
            </b-card-text>
          </b-tab>
        </b-tabs>
        <message-modal ref="messageModal"></message-modal>
        <confirmation-modal ref="confirmModal"></confirmation-modal>
      </div>
    </div>
  </div>
</template>

<script>
import { useSearch } from "../../../stores/search";
import { useManageAttributes } from "../../../stores/manage-attributes";
import MessagesApi from "../../../apis/MessagesApi";
import CampaignApi from "../../../apis/CampaignApi";
import InstancesApi from "../../../apis/InstancesApi";
import moment from "moment-timezone";
import datetime from "vuejs-datetimepicker";
import SmartList from "./SmartList.vue";
import MemberList from "./MemberList.vue";
import Statistics from "./Statistics.vue";

export default {
  props: {
    campaignId: String,
    permissions: {
      type: Array,
    },
  },
  components: {
    datetime,
    SmartList,
    MemberList,
    Statistics,
  },

  computed: {
    form() {
      return this.useManageAttributes.getForm;
    },
    name: {
      get() {
        return this.useManageAttributes.getForm.name;
      },
      set(value) {
        this.useManageAttributes.updateValue({
          key: "name",
          value: value,
        });
      },
    },
    description: {
      get() {
        return this.useManageAttributes.getForm.description;
      },
      set(value) {
        this.useManageAttributes.updateValue({
          key: "description",
          value: value,
        });
      },
    },
    template: {
      get() {
        return this.useManageAttributes.getForm.attributes.template;
      },
      set(value) {
        this.useManageAttributes.updateCampaignTemplate({
          key: "template",
          value: value,
        });
      },
    },
    timezone: {
      get() {
        return this.useManageAttributes.getForm.attributes.timezone;
      },
      set(value) {
        this.useManageAttributes.updateCampaignTemplate({
          key: "timezone",
          value: value,
        });
      },
    },
    executeDateTime: {
      get() {
        return this.useManageAttributes.getForm.attributes.executeDateTime;
      },
      set(value) {
        this.useManageAttributes.updateCampaignTemplate({
          key: "executeDateTime",
          value: value,
        });
      },
    },
    status: {
      get() {
        return this.useManageAttributes.getForm.attributes.status;
      },
      set(value) {
        this.useManageAttributes.updateCampaignTemplate({
          key: "status",
          value: value,
        });
      },
    },
    isActive: {
      get() {
        return this.useManageAttributes.getForm.isActive;
      },
      set(value) {
        this.useManageAttributes.updateValue({
          key: "isActive",
          value: value,
        });
      },
    },
    statistics() {
      return this.useManageAttributes.statistics;
    },
    states() {
      return this.useSearch.states;
    },
    pagination() {
      return this.useSearch.pagination;
    },
    attributes: {
      get() {
        return this.useManageAttributes.getForm.attributes;
      },
      set(attributes) {
        this.useManageAttributes.updateAttributes(attributes);
      },
    },
    errorMessage() {
      return this.useManageAttributes.errorMessage;
    },
    disabled() {
      return (
        this.fetching_programs ||
        this.fetching_messages ||
        this.fetching_persons ||
        this.status == "CANCEL" ||
        this.status == "SCHEDULE" ||
        this.status == "COMPLETED" ||
        this.status == "RUNNING"
      );
    },
    cancelDisabled() {
      return (
        this.fetching_programs ||
        this.fetching_messages ||
        this.fetching_persons ||
        this.disableForm ||
        this.status == "COMPLETED" ||
        this.status == "RUNNING" ||
        (this.permissions.indexOf("scheduler-edit") === -1 &&
          this.permissions.indexOf("scheduler-read") === -1)
      );
    },
    disableForm() {
      return this.useManageAttributes.disableForm
    },
    searchCriteria() {
      return this.useSearch.searchCriteria
    }
  },
  data() {
    return {
      instanceId: window.Laravel.selected_instance
        ? window.Laravel.selected_instance.id
        : null,
      csrf: null,
      route: null,
      method: "POST",
      CampaignApi: CampaignApi,
      options: { mode: "preview", mainMenuBar: false, statusBar: true },
      default_attribute: {
        template: {
          executeScript: "",
        },
      },
      currentTab: 0,
      types: [
        { value: null, text: this.$t("messages.type") },
        { value: "inbound", text: "Inbound" },
        { value: "outbound", text: "Outbound" },
      ],
      selected: null,
      timeZoneNames: [],
      timeZoneNamesObj: [],
      messagesList: [],
      outbound_messages: [],
      fetching_programs: false,
      fetching_messages: false,
      fetching_persons: false,
      useSearch: useSearch(),
      useManageAttributes: useManageAttributes(),
    };
  },
  created() {
    this.initForm();
    this.getTimeZones();
    this.onInstanceChange();

    this.useSearch.updateStates({
      showJsonEditor: false,
      showAttributeValues: true,
      allowBase64EncDec: true,
    });

    this.useManageAttributes.setDataTypes([
      {
        type: "String",
        disabled: false,
      },
      {
        type: "Number",
        disabled: false,
      },
      {
        type: "Boolean",
        disabled: false,
      },
      {
        type: "Advanced",
        disabled: false,
      },
      {
        type: "Text",
        disabled: true,
      },
    ]);
    this.useManageAttributes.updateErrors({
      name: false,
      description: false,
      attributes: false,
    });

    this.useManageAttributes.setPermissions(this.permissions);
    this.useManageAttributes.setDocumentId(this.campaignId);
    this.csrf = document.head.querySelector('meta[name="csrf-token"]').content;
  },
  methods: {
    initForm() {
      let formData = {
        result: {
          id: null,
          name: null,
          description: null,
          isActive: true,
          attributes: {
            messageTemplateId: null,
            executeTime: null,
            executeDate: null,
            executeDateTime: moment().format("YYYY-MM-DD HH:mm:ss"),
            timezone: null,
            status: null,
            template: null,
            personFilters: null,
          },
        },
      };
      this.useManageAttributes.initialize(formData);
      this.useSearch.setSearchMode("advanced");

      if (this.campaignId) {
        this.useManageAttributes.updateStates({
          fetching: true,
        });
      }
    },
    getTimeZones() {
      let timeZones = moment.tz.names();
      let offsetTmz = [];

      for (let i in timeZones) {
        offsetTmz.push(
          `(GMT${moment.tz(timeZones[i]).format("Z")}) ${timeZones[i]}`
        );
      }

      this.timeZoneNames = offsetTmz.sort();
      this.timeZoneNamesObj = this.timeZoneNames.map((id) => ({
        id: id.split(" ")[1],
        name: id,
      }));
    },
    hasError(field) {
      if (
        (!this.form[field] || this.form[field].length == 0) &&
        (!this.form.attributes[field] ||
          this.form.attributes[field].length == 0)
      ) {
        this.useManageAttributes.updateErrors({ [field]: true });
        return true;
      } else {
        this.useManageAttributes.updateErrors({ [field]: false });
        return false;
      }
    },
    fetchCampaign() {
      CampaignApi.find(this.campaignId, this.instanceId)
        .then((r) => {
          this.useManageAttributes.updateStates({
            fetching: false,
          });

          if (r.data.result.attributes) {
            // Try to find template on the available list
            if (r.data.result.attributes.messageTemplateId) {
              r.data.result.attributes.template = this.outbound_messages.find(
                (message) =>
                  message.id === r.data.result.attributes.messageTemplateId
              );
            }
          
            // Find timezone on the available list
            if (r.data.result.attributes.timezone) {
              r.data.result.attributes.timezone = this.timeZoneNamesObj.find(
                (timezone) => timezone.id === r.data.result.attributes.timezone
              );
            }
          
            r.data.result.attributes.executeDateTime =
              r.data.result.attributes.executeDate +
              " " +
              r.data.result.attributes.executeTime;

            // Read Smart list filter
            if (r.data.result.attributes.personFilters) {
              this.useSearch.setAdvancedSearch(r.data.result.attributes.personFilters);
            }
          
            this.useManageAttributes.initializeStatistics(r.data.result);
          }
          this.useManageAttributes.initialize(r.data);

          // // Set the Data Type and disable the field.
          this.useManageAttributes.setAttributeDataType({
            name: "executeScript",
            dataType: "Text",
          });
          this.useManageAttributes.toggleAttributeState({
            name: "executeScript",
            disableUpdate: true,
          });

          this.useManageAttributes.updateHeaders(r.headers);
        })
        .catch((e) => {
          this.useManageAttributes.updateStates({
            fetching: false,
          });
          this.$toast.error(
            "Error",
            this.$t("scheduler.unable_to_fetch_campaign")
          );
        });
    },
    createId() {
      let id = this.form.name.replace(/[^a-zA-Z0-9 ]/g, "-");
      return String(id).replace(/\s+/g, "-").toLowerCase();
    },
    onSubmit(e) {
      this.useSearch.updateStates({
        showErrors: true,
      });
      if (this.hasErrors()) {
        this.$toast.error("Error", this.$t("commons.form_error"));
        return;
      }
      if (this.campaignId) {
        this.confirmUpdateCampaign();
      } else {
        this.createCampaign();
      }
    },
    hasErrors() {
      return (
        this.hasError("name") ||
        this.hasError("description") ||
        this.hasError("timezone") ||
        this.hasError("template")
      );
    },
    getPayload() {
      let executeDateTime = moment(this.executeDateTime);

      return {
        payload: {
          id: this.campaignId || this.createId(),
          name: this.name,
          description: this.description,
          isActive: Boolean(this.form.isActive),
          attributes: {
            timezone: this.timezone.id,
            executeTime: executeDateTime.format("HH:mm:ss"),
            executeDate: executeDateTime.format("YYYY-MM-DD"),
            status: this.status,
            messageTemplateId: this.template.id,
            personFilters: this.searchCriteria,
          },
        },
      };
    },
    createCampaign() {
      this.useSearch.updateStates({
        saving: true,
        showSuccess: false,
      });
      this.useManageAttributes.setErrorMessage(null);

      let payload = this.getPayload();
      payload.payload.attributes.status = "DRAFT";

      CampaignApi.create(payload, this.instanceId)
        .then((r) => {
          this.useSearch.updateStates({
            showSuccess: true,
          });
          this.$toast.success(
            "Success",
            this.$t("scheduler.created_successfully")
          );
          window.location.href = this.$routes("program.campaigns.edit", {
            id: r.result.id,
          });
        })
        .catch((e) => {
          this.useSearch.updateStates({
            saving: false,
          });
          if (e.response.status == 409) {
            this.$toast.error("Error", this.$t("scheduler.already_exists"));
            this.useManageAttributes.setErrorMessage(this.$t("scheduler.already_exists"));
          } else if (e.response.status == 400) {
            this.$toast.error("Error", e.response.data.data.detail);
            this.useManageAttributes.setErrorMessage(e.response.data.data.detail);
          } else if (e.response.status == 422) {
            let err = AttributeUtils.convertToErrorMessage(
              e.response.data.errors
            );
            this.$toast.error("Error", err);
            this.useManageAttributes.setErrorMessage(err);
          } else {
            this.$toast.error("Error", this.$t("scheduler.create_failed"));
            this.useManageAttributes.setErrorMessage(this.$t("scheduler.create_failed"));
          }
        });
    },
    scheduleCampaign() {
      this.useSearch.updateStates({
        showErrors: true,
      });

      if (this.hasErrors()) {
        this.$toast.error("Error", this.$t("commons.form_error"));
        return;
      }

      let payload = this.getPayload();
      payload.payload.attributes.status = "SCHEDULE";
      this.updateCampaign(payload);
    },
    confirmCancelCampaign() {
      this.$refs.confirmModal
        .open(this.$t("scheduler.confirm_cancel_campaign"))
        .then((response) => {
          if (response) {
            this.cancelCampaign();
          }
        });
    },
    cancelCampaign() {
      let payload = this.getPayload();
      payload.payload.attributes.status = "CANCEL";

      this.updateCampaign(payload);
    },
    confirmUpdateCampaign() {
      this.$refs.confirmModal
        .open(this.$t("commons.confirm_update"))
        .then((response) => {
          if (response) {
            this.updateCampaign();
          }
        });
    },
    updateCampaign(payload) {
      this.useSearch.updateStates({
        saving: true,
        showSuccess: false,
      });
      this.useManageAttributes.setErrorMessage(null);

      let eTag = null;
      let headers = this.useManageAttributes.headers;

      if (headers && headers["ETag"] && headers["ETag"].length > 0) {
        eTag = headers["ETag"][0];
      }

      if (!payload) {
        payload = this.getPayload();
      }

      CampaignApi.update(payload, this.instanceId, this.campaignId)
        .then((r) => {
          this.useSearch.updateStates({
            showSuccess: true,
          });
          this.$toast.success(
            "Success",
            this.$t("scheduler.saved_successfully")
          );
          window.location.href = this.$routes("program.campaigns.edit", {
            id: r.data.result.id,
          });
        })
        .catch((e) => {
          let errorMessage = this.$t("scheduler.update_failed");
          this.useSearch.updateStates({
            saving: false,
          });

          if (e.response.status == 412) {
            errorMessage = this.$t("commons.optimistic_lock");
            this.useManageAttributes.setErrorMessage(errorMessage);
            this.invalid = true;
          } else if (e.response.status == 400) {
            this.$toast.error("Error", e.response.data.data.detail);
            this.useManageAttributes.setErrorMessage(e.response.data.data.detail);
          }

          this.$toast.error("Error", errorMessage);
        });
    },
    onInstanceChange() {
      this.fetching_programs = this.fetching_messages = true;

      InstancesApi.find(this.instanceId)
        .then((r) => {
          this.fetching_programs = false;
          this.useManageAttributes.updateHeaders(r.headers);
          this.useManageAttributes.setApprovalProcessStatus(r.data.result);

          let params = {
            returns: "id,attributes",
            filters: "attributes.type=outbound",
            limit: 100000,
          };

          // Add approval filter
          if (this.states.approvalProcessEnabled) {
            params.filters += ",status=APPROVED";
          }

          this.getMessages(params);
        })
        .catch((e) => {
          let errorMessage = this.$t("pharmaops.find_failed");
          if (e.response && e.response.status == 404) {
            errorMessage = this.$t("commons.not_found");
            this.useSearch.updateStates({
              invalid: true,
            });
          } else {
            this.fetching_programs = false;
          }
          this.$toast.error("Error", errorMessage);
          this.useManageAttributes.setErrorMessage(errorMessage);
          this.useManageAttributes.updateStates({
            fetching: false
          })
        });
    },
    getMessages(params) {
      MessagesApi.all(params, this.instanceId)
        .then((response) => {
          this.fetching_messages = false;
          response.result.forEach((element) => {
            let item = {
              id: element.id,
              type: "outbound",
              name: element.name,
              message: element.attributes.message,
            };
            this.outbound_messages.push(item);
          });
          if (this.campaignId) {
            this.fetchCampaign();
          }
        })
        .catch((e) => {
          this.useManageAttributes.updateStates({
            fetching: false,
          });
          this.fetching_messages = false;
          let errorMessage = this.$t("messages.search_failed");
          if (e.response && e.response.status == 400) {
            errorMessage = e.response.data.data.detail;
          }
          this.useManageAttributes.setErrorMessage(errorMessage);
          this.$toast.error("Error", errorMessage);
        });
    },
    setTab(val) {
      this.currentTab = val;
      const contentFrame = this.$refs.contentFrame;
      contentFrame.scrollIntoView({ behavior: "smooth" });
    },
  },
};
</script>
<style>
.multiselect__input {
  min-height: 32px;
  display: block;
  border-radius: 5px;
  font-size: 12px;
  padding: 0px;
}
.multiselect__tags {
  min-height: 38px;
}
.multiselect__placeholder {
  margin-bottom: 0;
}
.multiselect__single {
  font-size: 12px;
  font-family: inherit;
  font-size: inherit;
  color: #212529;
}
.datetime-picker input[data-v-4bd11526] {
  display: block;
  width: 100%;
  height: calc(1.6em + 0.75rem + 2px);
  padding: 0.375rem 0.75rem;
  font-size: 0.9rem;
  font-weight: 400;
  line-height: 1.6;
  color: #495057;
  border-radius: 0.25rem;
}
.nav-l[data-v-4bd11526],
.nav-l[data-v-4bd11526]:hover,
.nav-r[data-v-4bd11526],
.nav-r[data-v-4bd11526]:hover {
  background-color: #0095ff;
}
.year-month-wrapper[data-v-4bd11526] {
  background-color: #0095ff;
}
.days[data-v-4bd11526] {
  color: #0095ff;
}
.activePort[data-v-4bd11526],
.activePort[data-v-4bd11526]:hover {
  background-color: #0095ff;
}
.okButton[data-v-4bd11526] {
  color: #0095ff;
}
li.active[data-v-4bd11526] {
  background-color: #0095ff;
}
</style>
